import { useCallback, useSyncExternalStore } from 'react';

// Listed in `PERSISTENT_KEYS` in storageUtils, so it survives the `beforeunload`
// wipe on refresh but is still cleared by `clearVoteMateStorageOnLogout` on
// explicit sign-out.
const STORAGE_KEY = 'votemate_liked_candidates';

const readFromStorage = (): Set<string> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
};

const persist = (set: Set<string>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // localStorage may be full / disabled (private mode, quota) — fail silently.
  }
};

// Module-level state shared by every consumer of the hook so all
// `BallotCandidate` rows stay in sync.
let likedSet: Set<string> = readFromStorage();
const subscribers = new Set<() => void>();

const subscribe = (callback: () => void): (() => void) => {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
};

// Stable reference per state — `toggleLike` swaps `likedSet` to a new Set
// each mutation, so React's reference-equality check in
// `useSyncExternalStore` detects changes correctly.
const getSnapshot = (): Set<string> => likedSet;

// ── Server-sync escape hatches ────────────────────────────────────────────────
// Used by BallotRecordsProvider to mirror hearts to the signed-in user's
// current ballot record (PUT /user/ballots/{id}/selections). Guests keep the
// pure-localStorage behavior.

export const getLikedCandidateIds = (): string[] => Array.from(likedSet);

export const replaceLikedCandidates = (ids: string[]): void => {
  likedSet = new Set(ids);
  persist(likedSet);
  subscribers.forEach((cb) => cb());
};

export const subscribeLikedCandidates = (callback: () => void): (() => void) => subscribe(callback);

/**
 * Returns the current liked-candidate set plus an `isLiked` lookup and `toggleLike`
 * mutator. Backed by `localStorage` under `votemate_liked_candidates`. The key
 * is registered as a persistent key in `storageUtils`, so it survives refresh
 * but is wiped by `clearVoteMateStorageOnLogout` on explicit sign-out.
 *
 * Built on `useSyncExternalStore` for tearing-safe concurrent reads.
 *
 * Signed-in users' hearts sync cross-device: BallotRecordsProvider mirrors
 * this store to the current ballot record server-side. Guests stay
 * per-device until they sign in (hearts carry over on first sync).
 */
const useLikedCandidates = () => {
  const set = useSyncExternalStore(subscribe, getSnapshot);

  const isLiked = useCallback((candidateId: string) => set.has(candidateId), [set]);

  const toggleLike = useCallback((candidateId: string): boolean => {
    const next = new Set(likedSet);
    const willBeLiked = !next.has(candidateId);
    if (willBeLiked) next.add(candidateId);
    else next.delete(candidateId);
    likedSet = next;
    persist(likedSet);
    subscribers.forEach((cb) => cb());
    return willBeLiked;
  }, []);

  return { isLiked, toggleLike };
};

export default useLikedCandidates;
